<template>
  <div class="metadata-page">
    <PageHeader
      title="Metadata de enlaces"
      subtitle="Cómo se ven tus enlaces al compartirlos por WhatsApp, Facebook o Telegram"
    >
      <template #actions>
        <Button
          icon="pi pi-upload"
          label="Subir imagen"
          :loading="uploading"
          @click="fileInput?.click()"
        />
      </template>
    </PageHeader>

    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      class="hidden"
      @change="onFilePicked"
    />

    <div v-if="loading" class="text-center py-5 text-500">Cargando…</div>

    <div v-else class="metadata-content">
      <!-- ── Ámbito + formulario ────────────────────────────────────────── -->
      <div class="card">
        <div class="flex flex-column gap-1 mb-4">
          <label class="text-sm font-medium">Ámbito</label>
          <Dropdown
            v-model="selectedId"
            :options="scopeGroups"
            optionGroupLabel="label"
            optionGroupChildren="items"
            optionLabel="label"
            optionValue="id"
            class="w-full"
          />
          <small class="text-500">{{ scopeHelp }}</small>
        </div>

        <div class="flex flex-column gap-3">
          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Título</label>
            <InputText
              v-model="draft.title"
              :placeholder="inheritedPlaceholder('title')"
              @focus="focusedField = 'title'"
            />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Descripción</label>
            <Textarea
              v-model="draft.description"
              rows="3"
              autoResize
              :placeholder="inheritedPlaceholder('description')"
              @focus="focusedField = 'description'"
            />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Nombre del sitio</label>
            <InputText
              v-model="draft.siteName"
              :placeholder="inheritedPlaceholder('siteName')"
              @focus="focusedField = 'siteName'"
            />
          </div>

          <div v-if="availableVariables.length" class="flex flex-column gap-1">
            <small class="text-500">
              Hacé clic en una variable para insertarla en
              {{ focusedField === 'description' ? 'la descripción' : focusedField === 'siteName' ? 'el nombre del sitio' : 'el título' }}.
              Se reemplaza por el dato real de cada enlace.
            </small>
            <div class="flex flex-wrap gap-1">
              <Tag
                v-for="v in availableVariables"
                :key="v.key"
                :value="v.label"
                severity="info"
                style="cursor: pointer"
                @click="insertVariable(v.key)"
              />
            </div>
          </div>

          <div class="flex flex-wrap gap-2 justify-content-end pt-2">
            <Button
              v-if="currentEntry"
              label="Restablecer"
              icon="pi pi-undo"
              severity="secondary"
              text
              :loading="resetting"
              @click="confirmReset"
            />
            <Button label="Guardar" icon="pi pi-check" :loading="saving" @click="save" />
          </div>
        </div>
      </div>

      <!-- ── Vista previa ───────────────────────────────────────────────── -->
      <div class="card">
        <div class="text-sm font-medium mb-1">Vista previa</div>
        <small class="text-500">
          Así se verá el enlace{{ previewSampleLabel }}
          {{ previewImageUrl ? '.' : '(sin imagen: WhatsApp no deja mostrar un ícono chico).' }}
        </small>

        <div class="preview-card mt-3">
          <div v-if="previewImageUrl" class="preview-image">
            <img :src="previewImageUrl" alt="Imagen del preview" />
          </div>
          <div class="preview-body">
            <div class="preview-site">{{ previewSiteName }}</div>
            <div class="preview-title">{{ previewTitle }}</div>
            <div class="preview-description">{{ previewDescription }}</div>
          </div>
        </div>
      </div>

      <!-- ── Biblioteca de imágenes ─────────────────────────────────────── -->
      <div class="card metadata-images">
        <div class="flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
          <div class="text-sm font-medium">Imágenes</div>
          <small class="text-500">JPG, PNG, WebP o GIF · recomendado 1200×630 · hasta 5MB</small>
        </div>

        <EmptyState
          v-if="!images.length"
          icon="pi-image"
          title="Sin imágenes propias"
          description="Los enlaces usan la imagen por defecto de la app. Subí una para personalizarlos."
        >
          <template #action>
            <Button icon="pi pi-upload" label="Subir imagen" :loading="uploading" @click="fileInput?.click()" />
          </template>
        </EmptyState>

        <div v-else class="image-grid mt-3">
          <div
            v-for="img in images"
            :key="img.id"
            class="image-card"
            :class="{ selected: draft.imageId === img.id }"
          >
            <img :src="imageUrl(img.url)" :alt="img.originalName" />
            <div class="image-info">
              <div class="image-name" :title="img.originalName">{{ img.originalName }}</div>
              <div class="text-xs text-500">
                {{ img.width && img.height ? `${img.width}×${img.height} · ` : '' }}{{ formatSize(img.size) }}
              </div>
              <div v-if="usageOf(img.id).length" class="flex flex-wrap gap-1 mt-1">
                <Tag
                  v-for="uso in usageOf(img.id)"
                  :key="uso"
                  :value="uso"
                  severity="success"
                  class="text-xs"
                />
              </div>
              <div v-else class="text-xs text-400 mt-1">Sin usar</div>
            </div>
            <div class="image-actions">
              <Button
                v-if="draft.imageId === img.id"
                label="Quitar"
                icon="pi pi-times"
                size="small"
                severity="secondary"
                text
                @click="draft.imageId = null"
              />
              <Button
                v-else
                label="Usar acá"
                icon="pi pi-check"
                size="small"
                text
                @click="draft.imageId = img.id"
              />
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                v-tooltip="'Eliminar imagen'"
                @click="confirmDeleteImage(img)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { useRouterStore } from '@/store/router'
import { useAppToast } from '@/composables/useAppToast'
import {
  useLinkMetadata,
  metadataImageUrl,
  renderMetadataTemplate,
} from '@/composables/useLinkMetadata'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Tag from 'primevue/tag'
import ConfirmDialog from 'primevue/confirmdialog'

const route = useRouterStore()
const confirm = useConfirm()
const toast = useAppToast()
const metadataApi = useLinkMetadata()

// Sin imagen propia configurada, el enlace se comparte sin og:image (WhatsApp
// agranda cualquier ícono chico a tarjeta completa, así que no hay forma de
// mostrar un ícono "chico" ahí): la preview refleja eso mostrando la tarjeta
// sin imagen. Coincide con lo que arma el servidor en publicMeta.js.

const loading = ref(true)
const saving = ref(false)
const resetting = ref(false)
const uploading = ref(false)
const fileInput = ref(null)
const focusedField = ref('title')

const linkTypes = ref([])
const variables = ref([])
const entries = ref([])
const images = ref([])
const links = ref([])

// Variables ya resueltas para el ámbito elegido (las calcula el servidor con los
// datos reales del enlace); alimentan la vista previa mientras se escribe.
const previewVars = ref({})

const draft = reactive({ title: '', description: '', siteName: '', imageId: null })

// ── Ámbitos ──────────────────────────────────────────────────────────────────
// El id serializa el ámbito: 'default', 'type:<tipo>' o 'token:<tipo>:<token>'.
const selectedId = ref('default')

const selected = computed(() => {
  const [kind, ...rest] = selectedId.value.split(':')
  if (kind === 'type') return { scope: 'type', scopeKey: rest[0], linkType: rest[0] }
  if (kind === 'token') return { scope: 'token', scopeKey: rest.slice(1).join(':'), linkType: rest[0] }
  return { scope: 'default', scopeKey: '', linkType: null }
})

const typeLabel = (type) => linkTypes.value.find((t) => t.type === type)?.label || type

const scopeGroups = computed(() => {
  const groups = [
    { label: 'General', items: [{ id: 'default', label: 'Todos los enlaces' }] },
    {
      label: 'Por tipo de enlace',
      items: linkTypes.value.map((t) => ({ id: `type:${t.type}`, label: t.label })),
    },
  ]
  if (links.value.length) {
    groups.push({
      label: 'Enlace específico',
      items: links.value.map((l) => ({
        id: `token:${l.type}:${l.token}`,
        label: `${typeLabel(l.type)} · ${l.label}`,
      })),
    })
  }
  return groups
})

const scopeHelp = computed(() => {
  if (selected.value.scope === 'default') {
    return 'Valores base para todos los enlaces públicos de la congregación.'
  }
  if (selected.value.scope === 'type') {
    const cfg = linkTypes.value.find((t) => t.type === selected.value.linkType)
    return `${cfg?.description || ''} Lo que dejes vacío se hereda de "Todos los enlaces".`
  }
  return 'Metadata solo para este enlace. Lo que dejes vacío se hereda del tipo y del general.'
})

// ── Herencia ─────────────────────────────────────────────────────────────────
const entryFor = (scope, scopeKey) =>
  entries.value.find((e) => e.scope === scope && e.scopeKey === (scopeKey || ''))

const currentEntry = computed(() => entryFor(selected.value.scope, selected.value.scopeKey))

// Ámbitos de los que hereda el actual, del más específico al más general.
const inheritedChain = computed(() => {
  const { scope, linkType } = selected.value
  const chain = []
  if (scope === 'token' && linkType) chain.push(entryFor('type', linkType))
  if (scope !== 'default') chain.push(entryFor('default', ''))
  return chain.filter(Boolean)
})

const typeDefaults = computed(
  () => linkTypes.value.find((t) => t.type === selected.value.linkType)?.defaults || null
)

const inheritedValue = (field) => inheritedChain.value.map((e) => e[field]).find(Boolean) || ''

const inheritedPlaceholder = (field) => {
  const inherited = inheritedValue(field)
  if (inherited) return `Heredado: ${inherited}`
  if (field === 'siteName') return 'Heredado: nombre de la congregación'
  const fallback = typeDefaults.value?.[field === 'title' ? 'title' : 'description']
  return fallback ? `Por defecto: ${fallback}` : 'Por defecto: el de cada página'
}

const availableVariables = computed(() =>
  variables.value.filter(
    (v) =>
      v.types.includes('*') ||
      (selected.value.linkType && v.types.includes(selected.value.linkType))
  )
)

const insertVariable = (key) => {
  draft[focusedField.value] = `${draft[focusedField.value] || ''}{{${key}}}`
}

// ── Vista previa ─────────────────────────────────────────────────────────────
const effective = (field) =>
  draft[field] || inheritedValue(field === 'siteName' ? 'siteName' : field) || ''

const render = (text) => renderMetadataTemplate(text, previewVars.value).replace(/\s+/g, ' ').trim()

const previewTitle = computed(
  () => render(effective('title')) || typeDefaults.value?.title || 'Territorios'
)
const previewDescription = computed(
  () => render(effective('description')) || typeDefaults.value?.description || ''
)
const previewSiteName = computed(
  () => render(effective('siteName')) || previewVars.value.congregacion || 'Territorios'
)

const imageUrl = (url) => metadataImageUrl(url)

const previewImage = computed(() => {
  const id = draft.imageId || inheritedValue('imageId')
  return images.value.find((i) => i.id === id) || null
})

const previewImageUrl = computed(() => (previewImage.value ? imageUrl(previewImage.value.url) : null))

const previewSampleLabel = computed(() => {
  if (selected.value.scope === 'token') {
    const link = links.value.find((l) => l.token === selected.value.scopeKey)
    return link ? ` de "${link.label}"` : ''
  }
  return ''
})

// ── Uso de cada imagen ───────────────────────────────────────────────────────
const scopeLabel = (entry) => {
  if (entry.scope === 'default') return 'Todos los enlaces'
  if (entry.scope === 'type') return typeLabel(entry.scopeKey)
  const link = links.value.find((l) => l.token === entry.scopeKey)
  return link ? link.label : 'Un enlace'
}

const usageOf = (imageId) =>
  entries.value.filter((e) => e.imageId === imageId).map(scopeLabel)

const formatSize = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`

// ── Carga ────────────────────────────────────────────────────────────────────
const applyEntryToDraft = () => {
  const entry = currentEntry.value
  draft.title = entry?.title || ''
  draft.description = entry?.description || ''
  draft.siteName = entry?.siteName || ''
  draft.imageId = entry?.imageId || null
}

const loadPreviewVars = async () => {
  const { linkType, scope, scopeKey } = selected.value
  if (!linkType) {
    // El ámbito general no cuelga de ningún enlace: se usa el primero que haya
    // para poder mostrar valores reales en las variables.
    const anyType = linkTypes.value[0]?.type
    if (!anyType) return
    const res = await metadataApi.getPreview({ type: anyType })
    previewVars.value = res?.metadata?.vars || {}
    return
  }
  const res = await metadataApi.getPreview({
    type: linkType,
    token: scope === 'token' ? scopeKey : undefined,
  })
  previewVars.value = res?.metadata?.vars || {}
}

const load = async () => {
  const res = await metadataApi.getMetadata()
  if (res?.error) {
    toast.error('Ocurrió un error', res.error)
    return
  }
  linkTypes.value = res.linkTypes || []
  variables.value = res.variables || []
  entries.value = res.entries || []
  images.value = res.images || []
  links.value = res.links || []
  applyEntryToDraft()
  await loadPreviewVars()
}

onMounted(async () => {
  await load()
  // Se puede llegar desde la lista de enlaces con el ámbito ya elegido
  // (?type=salida&token=…); el watch se encarga de recargar el formulario.
  const { type, token } = route.params || {}
  if (type && linkTypes.value.some((t) => t.type === type)) {
    selectedId.value = token ? `token:${type}:${token}` : `type:${type}`
  }
  loading.value = false
})

watch(selectedId, async () => {
  applyEntryToDraft()
  focusedField.value = 'title'
  await loadPreviewVars()
})

// ── Guardar / restablecer ────────────────────────────────────────────────────
const save = async () => {
  saving.value = true
  try {
    const res = await metadataApi.saveEntry({
      scope: selected.value.scope,
      scopeKey: selected.value.scopeKey,
      linkType: selected.value.linkType,
      title: draft.title,
      description: draft.description,
      siteName: draft.siteName,
      imageId: draft.imageId,
    })
    if (res?.error) {
      toast.error('Ocurrió un error', res.error)
      return
    }
    await load()
    toast.success('Metadata guardada', 'Los próximos enlaces compartidos usarán estos datos.')
  } finally {
    saving.value = false
  }
}

const confirmReset = () => {
  confirm.require({
    message:
      selected.value.scope === 'default'
        ? '¿Borrar la metadata general? Los enlaces volverán a los textos por defecto.'
        : '¿Borrar la metadata de este ámbito? Volverá a heredar la del nivel superior.',
    header: 'Restablecer metadata',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Restablecer',
    rejectLabel: 'Cancelar',
    accept: async () => {
      resetting.value = true
      try {
        const res = await metadataApi.resetEntry(selected.value.scope, selected.value.scopeKey)
        if (res?.error) {
          toast.error('Ocurrió un error', res.error)
          return
        }
        await load()
      } finally {
        resetting.value = false
      }
    },
  })
}

// ── Imágenes ─────────────────────────────────────────────────────────────────
const onFilePicked = async (event) => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  uploading.value = true
  try {
    const res = await metadataApi.uploadImage(file)
    if (res?.error) {
      toast.error('No se pudo subir', res.error)
      return
    }
    images.value = [res.image, ...images.value]
    draft.imageId = res.image.id
    toast.success('Imagen subida', 'Guardá el ámbito para empezar a usarla.')
  } finally {
    uploading.value = false
  }
}

const confirmDeleteImage = (img) => {
  const usos = usageOf(img.id)
  confirm.require({
    message: usos.length
      ? `La imagen está activa en: ${usos.join(', ')}. Si la eliminás, esos enlaces vuelven a la imagen heredada.`
      : '¿Eliminar esta imagen?',
    header: 'Eliminar imagen',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      const res = await metadataApi.deleteImage(img.id)
      if (res?.error) {
        toast.error('Ocurrió un error', res.error)
        return
      }
      if (draft.imageId === img.id) draft.imageId = null
      await load()
    },
  })
}
</script>

<style scoped>
.metadata-page {
  text-align: left;
}
.metadata-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-4);
  padding: 0 var(--space-5) var(--space-5);
  align-items: start;
}
.metadata-images {
  grid-column: 1 / -1;
}
.card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow-1);
}
.hidden {
  display: none;
}

/* Tarjeta con la forma del preview que arman WhatsApp y Facebook. */
.preview-card {
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  max-width: 420px;
}
.preview-image {
  aspect-ratio: 1200 / 630;
  background: var(--surface-100);
}
.preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.preview-body {
  padding: var(--space-3);
}
.preview-site {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--label-secondary, #6b7280);
}
.preview-title {
  font-weight: 600;
  margin-top: 2px;
}
.preview-description {
  font-size: 0.85rem;
  color: var(--label-secondary, #6b7280);
  margin-top: 2px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--space-3);
}
.image-card {
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.image-card.selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px var(--primary-color);
}
.image-card img {
  width: 100%;
  aspect-ratio: 1200 / 630;
  object-fit: cover;
  display: block;
  background: var(--surface-100);
}
.image-info {
  padding: var(--space-2) var(--space-3);
  flex: 1;
}
.image-name {
  font-size: 0.85rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.image-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-2) var(--space-2);
}

@media (max-width: 900px) {
  .metadata-content {
    grid-template-columns: 1fr;
    padding: 0 var(--space-3) var(--space-4);
  }
}
</style>
